export const THEMES = ['light', 'dark', 'night'];

const shared = {
  radiusSm: '6px',
  radius: '8px',
  radiusLg: '12px',
  fontSans:
    "'Inter Variable', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontMono:
    "'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace",
};

export const palettes = {
  light: {
    isDark: false,
    bg: '#f5f6f8',
    surface: '#ffffff',
    surface2: '#f8f9fb',
    surfaceHover: '#f3f4f7',
    elevated: '#ffffff',
    sidebar: '#fbfbfc',
    border: '#e6e8ec',
    borderStrong: '#d5d9e0',
    text: '#0e1320',
    text2: '#4b5565',
    text3: '#6b7383',
    accent: '#4f46e5',
    accentHover: '#4338ca',
    accentSoft: 'rgba(79, 70, 229, 0.08)',
    accentSoftStrong: 'rgba(79, 70, 229, 0.14)',
    accentText: '#4338ca',
    onAccent: '#ffffff',
    success: '#047857',
    successSoft: 'rgba(5, 150, 105, 0.10)',
    danger: '#dc2626',
    dangerSoft: 'rgba(220, 38, 38, 0.08)',
    warning: '#b45309',
    warningSoft: 'rgba(217, 119, 6, 0.10)',
    shadowSm: '0 1px 2px rgba(16, 24, 40, 0.04)',
    shadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 2px 8px -2px rgba(16, 24, 40, 0.06)',
    shadowLg: '0 12px 32px -8px rgba(16, 24, 40, 0.14), 0 2px 6px rgba(16, 24, 40, 0.05)',
    overlay: 'rgba(15, 19, 32, 0.32)',
    skeleton: '#eceef2',
    skeletonShine: '#f6f7f9',
    chartGrid: '#eceef2',
    chartAxis: '#8a92a1',
    chart: ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#94a3b8'],
    chartPrev: '#a5adbb',
  },
  dark: {
    isDark: true,
    bg: '#0f1013',
    surface: '#16171b',
    surface2: '#1b1c21',
    surfaceHover: '#1f2026',
    elevated: '#1c1d22',
    sidebar: '#121316',
    border: '#26272e',
    borderStrong: '#33353d',
    text: '#ececef',
    text2: '#a6a8b2',
    text3: '#8a8d98',
    accent: '#5558e8',
    accentHover: '#4b4edc',
    accentSoft: 'rgba(124, 127, 247, 0.12)',
    accentSoftStrong: 'rgba(124, 127, 247, 0.20)',
    accentText: '#a5a7fb',
    onAccent: '#ffffff',
    success: '#34d399',
    successSoft: 'rgba(52, 211, 153, 0.12)',
    danger: '#f87171',
    dangerSoft: 'rgba(248, 113, 113, 0.12)',
    warning: '#fbbf24',
    warningSoft: 'rgba(251, 191, 36, 0.12)',
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadow: '0 1px 2px rgba(0, 0, 0, 0.3), 0 4px 12px -4px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 16px 40px -12px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.3)',
    overlay: 'rgba(0, 0, 0, 0.55)',
    skeleton: '#1f2026',
    skeletonShine: '#26272e',
    chartGrid: '#24252b',
    chartAxis: '#7c7f8a',
    chart: ['#7c7ff7', '#38bdf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#6b7280'],
    chartPrev: '#5b5e69',
  },
  night: {
    isDark: true,
    bg: '#070b14',
    surface: '#0c1220',
    surface2: '#101828',
    surfaceHover: '#131c2e',
    elevated: '#111a2b',
    sidebar: '#090e19',
    border: '#1a2336',
    borderStrong: '#243048',
    text: '#dde5f2',
    text2: '#97a3ba',
    text3: '#7a86a0',
    accent: '#3f62e8',
    accentHover: '#3557dc',
    accentSoft: 'rgba(109, 140, 255, 0.12)',
    accentSoftStrong: 'rgba(109, 140, 255, 0.20)',
    accentText: '#9db2ff',
    onAccent: '#ffffff',
    success: '#3dd68c',
    successSoft: 'rgba(61, 214, 140, 0.12)',
    danger: '#f47b7b',
    dangerSoft: 'rgba(244, 123, 123, 0.12)',
    warning: '#f2b84b',
    warningSoft: 'rgba(242, 184, 75, 0.12)',
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadow: '0 1px 2px rgba(0, 0, 0, 0.4), 0 4px 14px -4px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 18px 44px -12px rgba(0, 0, 0, 0.7), 0 2px 6px rgba(0, 0, 0, 0.4)',
    overlay: 'rgba(2, 5, 12, 0.6)',
    skeleton: '#121a2b',
    skeletonShine: '#1a2438',
    chartGrid: '#162036',
    chartAxis: '#6c7891',
    chart: ['#6d8cff', '#38bdf8', '#3dd68c', '#f2b84b', '#f472b6', '#a78bfa', '#56637d'],
    chartPrev: '#46526b',
  },
};

const toKebab = key => key.replace(/[A-Z0-9]/g, m => `-${m.toLowerCase()}`);

export const toCssVariables = name => {
  const palette = { ...shared, ...palettes[name] };
  const vars = {};
  Object.entries(palette).forEach(([key, value]) => {
    if (typeof value === 'string') {
      vars[`--wm-${toKebab(key)}`] = value;
    }
  });
  palette.chart.forEach((color, i) => {
    vars[`--wm-chart-${i + 1}`] = color;
  });
  return vars;
};

export const getAntdTheme = (name, algorithms) => {
  const p = palettes[name];
  return {
    algorithm: p.isDark ? algorithms.darkAlgorithm : algorithms.defaultAlgorithm,
    token: {
      colorPrimary: p.accent,
      colorInfo: p.accent,
      colorSuccess: p.success,
      colorError: p.danger,
      colorWarning: p.warning,
      colorPrimaryHover: p.accentHover,
      colorPrimaryActive: p.accentHover,
      colorLink: p.accentText,
      colorLinkHover: p.isDark ? p.text : p.accentHover,
      colorBgLayout: p.bg,
      colorBgContainer: p.surface,
      colorBgElevated: p.elevated,
      colorBgSpotlight: p.isDark ? p.borderStrong : '#1f2433',
      colorBorder: p.borderStrong,
      colorBorderSecondary: p.border,
      colorText: p.text,
      colorTextSecondary: p.text2,
      colorTextTertiary: p.text3,
      colorTextQuaternary: p.text3,
      colorTextPlaceholder: p.text3,
      colorFillAlter: p.surface2,
      colorFillSecondary: p.surfaceHover,
      controlItemBgHover: p.surfaceHover,
      controlItemBgActive: p.accentSoft,
      controlItemBgActiveHover: p.accentSoftStrong,
      controlOutline: p.accentSoft,
      fontFamily: shared.fontSans,
      fontSize: 14,
      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 6,
      controlHeight: 36,
      controlHeightLG: 40,
      controlHeightSM: 28,
      lineHeight: 1.5,
      boxShadow: p.shadowLg,
      boxShadowSecondary: p.shadowLg,
      motionDurationFast: '0.12s',
      motionDurationMid: '0.18s',
      motionDurationSlow: '0.24s',
      wireframe: false,
    },
  };
};
